import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  FiCode, 
  FiFolder,
  FiZap,
  FiTerminal,
  FiEye,
  FiDatabase,
  FiShare2,
  FiCloud,
  FiAward
} from 'react-icons/fi'
import { 
  IoMdColorWand,
  IoMdFlash,
  IoMdSpeedometer,
  IoMdRocket
} from 'react-icons/io'
import { VscFiles, VscTerminal, VscPreview, VscBracketDot } from 'react-icons/vsc'
import { FaFire, FaRobot, FaBolt } from 'react-icons/fa'

const Home = () => {
  const features = [
    {
      icon: <FaRobot />,
      title: 'AI Auto-Suggestions',
      description: 'Intelligent code completion with AI-powered suggestions',
      gradient: 'from-red-500 to-pink-500'
    },
    {
      icon: <VscFiles />,
      title: 'Folder Structure',
      description: 'Organize code with nested folders and file management',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: <FiTerminal />,
      title: 'Built-in Terminal',
      description: 'Execute commands and run code directly in browser',
      gradient: 'from-amber-500 to-orange-500'
    },
    {
      icon: <FiEye />,
      title: 'Live Preview',
      description: 'Real-time HTML/CSS preview with responsive views',
      gradient: 'from-rose-500 to-red-500'
    },
    {
      icon: <FaBolt />,
      title: 'Fast Compilation',
      description: 'Compile and run 10+ languages instantly',
      gradient: 'from-red-500 to-orange-500'
    },
    {
      icon: <IoMdColorWand />,
      title: 'Red Theme',
      description: 'Beautiful red theme with syntax highlighting',
      gradient: 'from-primary-600 to-red-500'
    }
  ]

  const supportedLanguages = [
    'JavaScript', 'TypeScript', 'Python', 'HTML/CSS',
    'Java', 'C++', 'PHP', 'Ruby', 'Go', 'Rust'
  ]

  const stats = [
    { value: '10K+', label: 'Lines Supported', icon: <FiDatabase /> },
    { value: '10+', label: 'Languages', icon: <VscBracketDot /> },
    { value: '5MB', label: 'Local Storage', icon: <FiCloud /> },
    { value: '⚡', label: 'Instant Compile', icon: <IoMdFlash /> }
  ]

  return (
    <div className="min-h-screen pt-16 md:pt-8 pb-8">
      <div className="container mx-auto px-3 sm:px-4">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-6xl mx-auto mb-8 md:mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-gradient-to-r from-primary-500/10 to-red-500/10 border border-primary-500/20 mb-4 md:mb-6">
            <FaFire className="text-primary-600 dark:text-primary-400 animate-pulse text-sm md:text-base" />
            <span className="text-xs md:text-sm font-medium text-primary-600 dark:text-primary-400">
              Advanced Code Editor with Red Theme
            </span>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6">
            <span className="bg-gradient-to-r from-primary-600 via-red-500 to-pink-500 bg-clip-text text-transparent">
              CodeFire
            </span>
            <br />
            <span className="text-2xl md:text-4xl lg:text-6xl text-gray-900 dark:text-white">
              Ignite Your Code
            </span>
          </h1>
          
          <p className="text-base md:text-xl text-gray-600 dark:text-gray-300 mb-6 md:mb-10 max-w-3xl mx-auto px-4">
            Professional-grade code editor with <span className="text-primary-600 dark:text-primary-400 font-semibold">red theme</span>, 
            auto-suggestions, folder structure, terminal, and live preview. 
            Write, compile, and share code—all in your browser.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 px-4">
            <Link to="/editor" className="w-full sm:w-auto">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full px-6 py-3 md:px-8 md:py-4 rounded-xl bg-gradient-to-r from-primary-600 to-red-500 text-white font-semibold text-base md:text-lg hover:from-primary-700 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl shadow-primary-500/25"
              >
                <span className="flex items-center justify-center space-x-2">
                  <FiCode className="w-5 h-5" />
                  <span>Start Coding Now</span>
                </span>
              </motion.button>
            </Link>
            <Link to="/explore" className="w-full sm:w-auto">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full px-6 py-3 md:px-8 md:py-4 rounded-xl bg-white dark:bg-gray-800 border-2 border-primary-500 text-primary-600 dark:text-primary-400 font-semibold text-base md:text-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all duration-300"
              >
                <span className="flex items-center justify-center space-x-2">
                  <FiFolder className="w-5 h-5" />
                  <span>Explore Projects</span>
                </span>
              </motion.button>
            </Link>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${stat.gradient || 'from-primary-500 to-red-500'} flex items-center justify-center`}>
                  <div className="text-lg md:text-xl text-white">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-primary-600 dark:text-primary-400">
                  {stat.value}
                </div>
              </div>
              <div className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Features */}
        <div className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 md:mb-12 text-gray-900 dark:text-white px-4">
            Professional <span className="text-primary-600 dark:text-primary-400">Features</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <div className="text-xl md:text-2xl text-white">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-gray-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Supported Languages */}
        <div className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 md:mb-8 text-gray-900 dark:text-white">
            Supported <span className="text-primary-600 dark:text-primary-400">Languages</span>
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-8 shadow-lg">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {supportedLanguages.map((lang, index) => (
                <motion.div
                  key={lang}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                  className="px-3 py-2 md:px-4 md:py-3 rounded-lg bg-gradient-to-r from-primary-50 to-red-50 dark:from-gray-900 dark:to-primary-900/20 text-center hover:from-primary-100 hover:to-red-100 dark:hover:from-gray-800 dark:hover:to-primary-900/30 transition-all duration-300 border border-primary-200 dark:border-primary-800/30"
                >
                  <span className="font-medium text-gray-900 dark:text-white text-sm md:text-base">{lang}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
          className="relative bg-gradient-to-r from-primary-600 to-red-600 rounded-2xl p-6 md:p-8 lg:p-12 text-center shadow-2xl overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse-glow"></div>
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
              Ready to <span className="text-yellow-300">Ignite</span> Your Code?
            </h2>
            <p className="text-white/90 text-base md:text-xl mb-6 md:mb-8 max-w-2xl mx-auto px-4">
              Experience professional-grade coding environment with <span className="font-bold">red theme</span>.
              All features, completely free.
            </p>
            <Link to="/editor">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 md:px-10 md:py-4 rounded-xl bg-white text-primary-600 font-bold text-base md:text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center justify-center space-x-2">
                  <IoMdRocket className="w-5 h-5 md:w-6 md:h-6" />
                  <span>Launch CodeFire</span>
                </span>
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Home